import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@akuna/ui";
import { Button } from "@akuna/ui";
import { useCreateProductSubtype } from "@/hooks/useProductSubtypes";
import type { ProductSubtypeRow } from "@/hooks/useProductSubtypes";
import { useTranslation } from "react-i18next";

interface CreateProductSubtypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubtypeCreated?: (subtype: ProductSubtypeRow) => void;
}

export function CreateProductSubtypeDialog({
  open,
  onOpenChange,
  onSubtypeCreated,
}: CreateProductSubtypeDialogProps) {
  const { t } = useTranslation("erp");
  const createProductSubtype = useCreateProductSubtype();
  const createProductSubtypeSchema = z.object({
    name: z.string().min(2, t("dialogs.createSubtype.validationNameMin")),
  });

  type CreateProductSubtypeFormValues = z.infer<typeof createProductSubtypeSchema>;
  const form = useForm<CreateProductSubtypeFormValues>({
    resolver: zodResolver(createProductSubtypeSchema),
    defaultValues: { name: "" },
  });

  useEffect(() => {
    if (open) {
      form.reset({ name: "" });
    }
  }, [open, form]);

  const onSubmit = async (values: CreateProductSubtypeFormValues) => {
    try {
      const created = await createProductSubtype.mutateAsync({ name: values.name });
      onSubtypeCreated?.(created);
      onOpenChange(false);
    } catch {
      // Error already handled in mutation onError
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("dialogs.createSubtype.title")}</DialogTitle>
          <DialogDescription>
            {t("dialogs.createSubtype.description")}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("dialogs.createSubtype.name")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("dialogs.createSubtype.placeholder")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t("common:cancel")}
              </Button>
              <Button type="submit" disabled={createProductSubtype.isPending}>
                {createProductSubtype.isPending ? t("dialogs.createSubtype.saving") : t("common:save")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
